import { useEffect, useState } from "react";
import { useApi } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Filter, X } from "lucide-react";

const EMPTY = {
  title: "", first_name: "", surname: "",
  grand_rank: "", provincial_rank: "", email: "", active: true,
};

export default function AdminCraftMasterPage() {
  const api = useApi();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  // "Filter Active" hides rows where active===false.
  const [activeFilterOn, setActiveFilterOn] = useState(false);

  const visibleEntries = activeFilterOn ? entries.filter((e) => e.active) : entries;

  const load = async () => {
    setLoading(true);
    try {
      const r = await api("/admin/craft-master");
      if (r.ok) { const d = await r.json(); setEntries(d.entries || []); }
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const openCreate = () => { setEditing({}); setForm(EMPTY); };
  const openEdit = (row) => { setEditing(row); setForm({
    title: row.title || "", first_name: row.first_name, surname: row.surname,
    grand_rank: row.grand_rank || "", provincial_rank: row.provincial_rank || "",
    email: row.email || "", active: !!row.active,
  }); };

  const submit = async () => {
    if (!form.first_name.trim() || !form.surname.trim()) {
      toast.error("First name and surname are required.");
      return;
    }
    setBusy(true);
    try {
      const isEdit = !!editing?.entry_id;
      const r = await api(
        isEdit ? `/admin/craft-master/${editing.entry_id}` : "/admin/craft-master",
        { method: isEdit ? "PATCH" : "POST", body: JSON.stringify(form) },
      );
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.detail || "Save failed"); return; }
      toast.success(isEdit ? "Entry updated" : "Entry added");
      setEditing(null);
      await load();
    } finally { setBusy(false); }
  };

  const del = async () => {
    if (!confirmDelete) return;
    setBusy(true);
    try {
      const r = await api(`/admin/craft-master/${confirmDelete.entry_id}`, { method: "DELETE" });
      if (!r.ok) { const d = await r.json().catch(()=>({})); toast.error(d.detail || "Delete failed"); return; }
      toast.success("Entry deleted");
      setConfirmDelete(null);
      await load();
    } finally { setBusy(false); }
  };

  const combinedRanks = (e) => [e.grand_rank, e.provincial_rank].filter(Boolean).join(" / ");

  return (
    <div>
      <PageHeader
        title="Middlesex Craft Complimentary Ticket Management"
        subtitle="Provincial Grand Lodge of Middlesex master list - Add, Edit and Delete"
        testId="craft-master-header"
      />

      <Card className="border-2 border-red-500/60 p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm text-muted-foreground">
            {loading
              ? "Loading…"
              : activeFilterOn
                ? `${visibleEntries.length} of ${entries.length} entries (active only)`
                : `${entries.length} entries`}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setActiveFilterOn((v) => !v)}
              data-testid="craft-filter-toggle"
              aria-pressed={activeFilterOn}
            >
              {activeFilterOn
                ? <X size={16} className="mr-2 text-red-600" />
                : <Filter size={16} className="mr-2" />}
              Filter Active
            </Button>
            <Button onClick={openCreate} data-testid="craft-new">
              <Plus size={16} className="mr-2" /> New entry
            </Button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b">
                <th className="py-2 pr-3 text-red-600 font-semibold">#</th>
                <th className="py-2 pr-3 text-red-600 font-semibold">Title</th>
                <th className="py-2 pr-3 text-red-600 font-semibold">First name</th>
                <th className="py-2 pr-3 text-red-600 font-semibold">Surname</th>
                <th className="py-2 pr-3 text-red-600 font-semibold">Ranks (Grand / Prov)</th>
                <th className="py-2 pr-3 text-red-600 font-semibold">Email</th>
                <th className="py-2 pr-3 text-red-600 font-semibold">Active</th>
                <th className="py-2 pr-3"></th>
              </tr>
            </thead>
            <tbody>
              {visibleEntries.map((e, i) => (
                <tr key={e.entry_id} className="border-b hover:bg-red-50" data-testid={`craft-row-${e.entry_id}`}>
                  <td className="py-2 pr-3 text-muted-foreground">{i + 1}</td>
                  <td className="py-2 pr-3">{e.title}</td>
                  <td className="py-2 pr-3">{e.first_name}</td>
                  <td className="py-2 pr-3">{e.surname}</td>
                  <td className="py-2 pr-3">{combinedRanks(e)}</td>
                  <td className="py-2 pr-3">{e.email}</td>
                  <td className="py-2 pr-3">{e.active ? "Yes" : "No"}</td>
                  <td className="py-2 pr-3 flex gap-1 justify-end">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(e)}
                            data-testid={`craft-edit-${e.entry_id}`}>
                      <Pencil size={14} />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(e)}
                            data-testid={`craft-delete-${e.entry_id}`}>
                      <Trash2 size={14} className="text-red-600" />
                    </Button>
                  </td>
                </tr>
              ))}
              {!loading && visibleEntries.length === 0 && (
                <tr><td colSpan={8} className="py-6 text-center text-muted-foreground italic">
                  {activeFilterOn
                    ? "No active entries."
                    : 'No entries yet. Add the first one using "New entry".'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o && !busy) setEditing(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing?.entry_id ? "Edit entry" : "New entry"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-1">
                <Label>Title</Label>
                <Input data-testid="craft-form-title" value={form.title}
                       onChange={(e) => setForm({ ...form, title: e.target.value })} />
              </div>
              <div className="space-y-1 col-span-2">
                <Label>First name</Label>
                <Input data-testid="craft-form-first" value={form.first_name}
                       onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Surname</Label>
              <Input data-testid="craft-form-surname" value={form.surname}
                     onChange={(e) => setForm({ ...form, surname: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label>Grand rank</Label>
                <Input data-testid="craft-form-grand" value={form.grand_rank}
                       onChange={(e) => setForm({ ...form, grand_rank: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label>Provincial rank</Label>
                <Input data-testid="craft-form-prov" value={form.provincial_rank}
                       onChange={(e) => setForm({ ...form, provincial_rank: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Email</Label>
              <Input type="email" data-testid="craft-form-email" value={form.email}
                     onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <label className="flex items-center gap-2 text-sm select-none">
              <input type="checkbox" data-testid="craft-form-active"
                     checked={form.active}
                     onChange={(e) => setForm({ ...form, active: e.target.checked })} />
              Active
            </label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)} disabled={busy}>Cancel</Button>
            <Button onClick={submit} disabled={busy} data-testid="craft-form-save">
              {busy ? "Saving…" : (editing?.entry_id ? "Save changes" : "Add entry")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => { if (!o && !busy) setConfirmDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete entry?</AlertDialogTitle>
            <AlertDialogDescription>
              Remove <span className="font-medium text-foreground">
                {[confirmDelete?.title, confirmDelete?.first_name, confirmDelete?.surname].filter(Boolean).join(" ")}
              </span> from the master list?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); del(); }} disabled={busy}
                               data-testid="craft-delete-confirm">
              {busy ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
